import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { INVESTOR } from '@/constants/testIds';

const LINKS = [
  { id: 'about', label: 'À propos' },
  { id: 'thesis', label: 'Thèse' },
  { id: 'portfolio', label: 'Portefeuille' },
];

const EASE = [0.19, 1, 0.22, 1];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <motion.header
      data-testid={INVESTOR.nav}
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2, ease: EASE }}
      className="fixed top-0 left-0 right-0 z-50 transition-colors duration-500"
      style={{
        backgroundColor: scrolled || open ? 'rgba(10,11,14,0.82)' : 'transparent',
        backdropFilter: scrolled || open ? 'blur(16px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(255,255,255,0.06)' : '1px solid transparent',
      }}
    >
      <nav className="mx-auto max-w-screen-2xl px-6 md:px-12 h-20 flex items-center justify-between">
        <a href="#top" className="font-display text-2xl tracking-tight text-foreground">
          Zukunft Real Estate<span className="text-primary"> GmbH</span>
        </a>

        {/* Desktop links */}
        <div className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={INVESTOR.navLink(l.id)}
              className="link-underline font-mono text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/verification"
            data-testid="nav-link-verification"
            className="link-underline font-mono text-xs uppercase tracking-[0.22em] text-primary hover:text-primary transition-colors duration-300"
          >
            Vérification / KYC
          </Link>
        </div>

        {/* Desktop CTA */}
        <a
          href="#contact"
          data-testid={INVESTOR.navContact}
          className="hidden md:inline-flex group relative items-center gap-2 border border-primary/40 px-5 py-2.5 font-mono text-[0.7rem] uppercase tracking-[0.2em] text-primary overflow-hidden"
        >
          <span className="absolute inset-0 bg-primary translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.19,1,0.22,1)]" />
          <span className="relative group-hover:text-primary-foreground transition-colors duration-500">
            Prendre contact
          </span>
        </a>

        {/* Mobile hamburger */}
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
          className="md:hidden flex h-11 w-11 items-center justify-center border border-border text-foreground"
        >
          {open ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
        </button>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="md:hidden overflow-hidden border-t border-border bg-[rgba(10,11,14,0.96)] backdrop-blur-xl"
          >
            <div className="px-6 py-8 flex flex-col gap-1">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.id}`}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 + i * 0.05, ease: EASE }}
                  className="font-display font-light text-3xl tracking-tight text-foreground hover:text-primary transition-colors duration-300 py-2"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.div
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.06 + LINKS.length * 0.05, ease: EASE }}
              >
                <Link
                  to="/verification"
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-link-verification"
                  className="block font-display font-light text-3xl tracking-tight text-primary py-2"
                >
                  Vérification / KYC
                </Link>
              </motion.div>

              <a
                href="#contact"
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-contact"
                className="mt-6 inline-flex items-center justify-center border border-primary px-5 py-4 font-mono text-[0.7rem] uppercase tracking-[0.22em] text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
              >
                Prendre contact
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
